import { SEARCH_MAX_QUERY_LENGTH, SEARCH_MIN_QUERY_LENGTH } from '@gty/common';

import { cn } from '../../ui/cn';
import { Icon } from '../../ui/icons';
import { useDismiss } from '../useDismiss';
import { SEARCH_GROUPS, type SearchHit } from './search-model';
import { useGlobalSearch, type GlobalSearchState } from './useGlobalSearch';

/**
 * The TopBar's global search — an ARIA combobox over nine entity types.
 *
 * Renders `useGlobalSearch` and nothing else. Every rule that could be got
 * wrong (when to fire, what the highlight may address, what Escape closes)
 * lives in the hook; this file decides only what it looks like.
 *
 * WHAT REPLACED WHAT: this box was once a permanently `disabled` input whose
 * placeholder advertised invoices while the server indexed only leads,
 * customers, bookings and tours. Part D (2026-11-03) closed that gap — the
 * server now also searches invoices, payments/receipts, credit notes,
 * vouchers and expenses (role-gated) — so the placeholder below names the
 * shorter, representative set rather than all nine; `SearchStatus`'s "no
 * results" hint below is the one place that spells out the complete list.
 */

const LISTBOX_ID = 'global-search-listbox';

/** A hit plus its position in the flattened list the arrow keys walk. */
interface IndexedHit {
  hit: SearchHit;
  index: number;
}

export function GlobalSearch(): JSX.Element {
  const search = useGlobalSearch();
  const { open, close } = search;

  /*
    Outside pointer-down closes the panel. The Escape half of this hook is
    intentionally never reached from inside the combobox — the handler in
    `useGlobalSearch` stops the event before it can get to `document`, so
    dismissing this panel cannot also collapse the notification tray.
  */
  const containerRef = useDismiss(open, close);

  const activeHit = search.activeIndex >= 0 ? search.hits[search.activeIndex] : undefined;

  return (
    <div
      ref={containerRef}
      className="relative hidden max-w-[440px] flex-1 md:block"
      data-testid="global-search"
    >
      <Icon
        name="search"
        size={16}
        className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-neutral-500"
      />
      {/*
        `aria-activedescendant` is omitted entirely rather than set to an empty
        string when nothing is highlighted: a reference to an id that is not in
        the document makes screen readers announce a stale target, which is
        worse than announcing none. The hook guarantees the index is either a
        real row or -1, so this can never dangle.

        `maxLength` mirrors the server's own cap. Over-long terms are REJECTED
        with a 400 rather than truncated, so capping the box makes that 400
        unreachable — the constraint is enforced where the user can see it
        happen, not reported as an error afterwards.
      */}
      <input
        type="search"
        role="combobox"
        aria-label="Search leads, customers, bookings, tours, invoices, payments, credit notes, vouchers and expenses"
        aria-expanded={open}
        aria-controls={LISTBOX_ID}
        aria-autocomplete="list"
        aria-haspopup="listbox"
        aria-activedescendant={activeHit?.domId}
        autoComplete="off"
        spellCheck={false}
        maxLength={SEARCH_MAX_QUERY_LENGTH}
        value={search.term}
        onChange={(event) => {
          search.setTerm(event.target.value);
        }}
        onFocus={search.openIfTyped}
        onKeyDown={search.onKeyDown}
        placeholder="Search leads, bookings, invoices, expenses…"
        data-testid="global-search-input"
        className="w-full rounded-[8px] border border-neutral-300 bg-canvas py-2.5 pl-9 pr-3 text-[13.5px] text-ink outline-none placeholder:text-neutral-500 focus:border-brand focus:bg-surface-raised"
      />

      {open && (
        <div
          data-testid="global-search-panel"
          className="absolute left-0 right-0 top-[46px] z-[60] animate-gy-in-fast overflow-hidden rounded-[10px] border border-neutral-300 bg-surface-raised shadow-menu"
        >
          <SearchStatus search={search} />
          <SearchResults search={search} />
        </div>
      )}
    </div>
  );
}

/**
 * THE LISTBOX IS ALWAYS RENDERED WHILE THE PANEL IS OPEN, even with nothing in
 * it. `aria-controls` on the input names this id, and an `aria-controls`
 * pointing at an element that is not in the document is an invalid reference —
 * so the element must exist for as long as the input claims to control it. The
 * status messages are its SIBLING rather than its child for the same reason in
 * reverse: a `role="listbox"` may only contain options and groups.
 */
function SearchResults({ search }: { search: GlobalSearchState }): JSX.Element {
  return (
    <div
      id={LISTBOX_ID}
      role="listbox"
      aria-label="Search results"
      data-testid="global-search-listbox"
      className="max-h-[380px] overflow-y-auto"
    >
      {SEARCH_GROUPS.map((group) => {
        /*
          Positions are taken FROM the flattened list rather than recomputed
          from the DTO. It is the same list the arrow keys walk, so the
          highlighted row and the keyboard cannot end up pointing at different
          records — and the fixed group order lives in exactly one place.
        */
        const groupHits: IndexedHit[] = search.hits
          .map((hit, index) => ({ hit, index }))
          .filter(({ hit }) => hit.group === group.key);

        // Empty groups render NOTHING — no header, no separator. A heading with
        // no rows under it reads as a loading failure.
        if (groupHits.length === 0) return null;

        const headerId = `global-search-group-${group.key}`;

        return (
          <div key={group.key} role="group" aria-labelledby={headerId}>
            <div
              id={headerId}
              role="presentation"
              data-testid={headerId}
              className="flex items-center gap-[6px] border-b border-neutral-200 bg-neutral-100 px-[15px] py-[7px] text-[10.5px] font-bold uppercase leading-none tracking-caps text-neutral-600"
            >
              <Icon name={group.icon} size={11} className="text-neutral-500" />
              {group.label}
            </div>
            {groupHits.map(({ hit, index }) => (
              <ResultRow
                key={hit.item.id}
                hit={hit}
                index={index}
                active={index === search.activeIndex}
                onHover={search.setActiveIndex}
                onPick={search.goTo}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

/**
 * The mutually exclusive things the panel can say instead of results.
 *
 * `role="status"` on a polite live region, so a screen-reader user hears "no
 * results" without focus ever leaving the input.
 */
function SearchStatus({ search }: { search: GlobalSearchState }): JSX.Element | null {
  const base = 'px-[15px] py-4 text-center text-[12px] leading-[1.5]';

  if (search.showMinLengthHint) {
    return (
      <p role="status" data-testid="global-search-hint" className={cn(base, 'text-neutral-600')}>
        Type at least {SEARCH_MIN_QUERY_LENGTH} characters
      </p>
    );
  }

  if (search.isLoading) {
    return (
      <p role="status" data-testid="global-search-loading" className={cn(base, 'text-neutral-600')}>
        Searching…
      </p>
    );
  }

  if (search.isError) {
    return (
      <p role="alert" data-testid="global-search-error" className={cn(base, 'text-danger-ink')}>
        Search is unavailable right now. Try again.
      </p>
    );
  }

  if (search.showNoResults) {
    return (
      <p role="status" data-testid="global-search-empty" className={cn(base, 'text-neutral-600')}>
        No results found for “{search.debouncedTerm}”
        <span className="mt-1 block text-[11px] text-neutral-500">
          Leads, customers, bookings, tours, invoices, payments, credit notes, vouchers and
          expenses are searched by name and by reference — each only within what your role can
          view.
        </span>
      </p>
    );
  }

  return null;
}

interface ResultRowProps {
  hit: SearchHit;
  index: number;
  active: boolean;
  onHover: (index: number) => void;
  onPick: (hit: SearchHit) => void;
}

function ResultRow({ hit, index, active, onHover, onPick }: ResultRowProps): JSX.Element {
  return (
    /*
      `onMouseDown` with `preventDefault`, NOT `onClick`.

      A plain click would blur the input first, `useDismiss` would see the
      pointer-down, and the row would be unmounted before its click handler ever
      ran — the classic "the dropdown item does nothing" bug. Handling
      pointer-down and refusing the blur keeps the row alive long enough to
      navigate.

      The highlight uses `bg-brand/[.05]` in ARBITRARY form. Tailwind's opacity
      modifier accepts only its own scale (…/5, /10, /20…); an off-scale value
      such as `bg-brand/7` compiles to NOTHING AT ALL, which would leave the
      highlighted row visually identical to the others while tsc and every unit
      test stayed green.
    */
    <div
      id={hit.domId}
      role="option"
      aria-selected={active}
      data-testid="global-search-option"
      data-index={index}
      data-entity={hit.item.type}
      onMouseDown={(event) => {
        event.preventDefault();
        onPick(hit);
      }}
      onMouseEnter={() => {
        onHover(index);
      }}
      className={cn(
        'flex cursor-pointer items-center gap-3 border-b border-neutral-200 px-[15px] py-[10px] last:border-b-0',
        active ? 'bg-brand/[.05]' : 'hover:bg-neutral-100',
      )}
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[12.8px] font-semibold leading-[1.35] text-ink">
          {hit.item.title}
        </span>
        {hit.item.subtitle.length > 0 && (
          <span className="mt-[3px] block truncate text-[11.5px] leading-[1.4] text-neutral-600">
            {hit.item.subtitle}
          </span>
        )}
      </span>
      {/*
        The ref is DISPLAYED because it is what people quote to each other — and
        it is displayed ONLY. Navigation uses `hit.to`, built from the record's
        id; see `routeForHit`.
      */}
      <span className="flex-none text-[10.5px] leading-none text-neutral-500">{hit.item.ref}</span>
    </div>
  );
}
